import { IconCircleDot } from '@tabler/icons-react'

import { WORKER_TYPES } from '../workers/constants'

import { SIGN_ON_TYPES } from './constants'

import { Pill } from '@/components/shared-components/BUI/Pill'
import { FormatDateStringType } from '@/context/timezoneContext'
import { convertEnumToString } from '@/utils/index'

import { CreatedFromContext, IntegrationType } from 'types/api.graphql'

type SignOnType = {
  id: string
  worker?: {
    id?: string
    user: {
      firstName: string
      lastName: string
    }
  }
  visitor?: {
    id?: string
    user: {
      firstName: string
      lastName: string
    }
  }
}

type SignOnTypeSignOn = {
  builderGenerated?: boolean
  integrationGenerated?: boolean
  integrationType?: IntegrationType | string
  contextMetadata?: {
    createdFrom?: string
  }
}

export const signOnType = (signOn: SignOnTypeSignOn) => {
  if ('builderGenerated' in signOn) {
    if (signOn.builderGenerated) return SIGN_ON_TYPES.MANUALLY_CREATED_SIGN_ON
  }

  if ('integrationGenerated' in signOn) {
    if (
      signOn.integrationGenerated &&
      signOn.integrationType === IntegrationType.IronLogic
    )
      return SIGN_ON_TYPES.GATE_ACCESS_CREATED_SIGN_ON
  }

  if ('contextMetadata' in signOn) {
    if (signOn.contextMetadata?.createdFrom === CreatedFromContext.Form) {
      return SIGN_ON_TYPES.FORM_CREATED_SIGN_ON
    }
  }
}

export const renderSignOnTypeTag = (signOn: SignOnTypeSignOn) => {
  if (!signOn) return null

  const type = signOnType(signOn)

  return type ? (
    <Pill
      whiteBg
      size="small"
      variant="info"
      icon={<IconCircleDot size={16} />}
      label={convertEnumToString(type)}
    />
  ) : null
}

const getSignOnUser = (signOn: SignOnType) => {
  if (signOn.worker) {
    return signOn.worker.user
  } else if (signOn.visitor) {
    return signOn.visitor.user
  }
  return { firstName: '', lastName: '' }
}

export const getSignOnUserName = (signOn: SignOnType) => {
  if (!signOn) return ''

  const { firstName, lastName } = getSignOnUser(signOn)

  return `${firstName} ${lastName}`
}

type SignOnRecord = {
  workerType: string
  signOnType: string
  createdAt: string
  manualSignedOnAt?: string
  createdFrom?: string
  formName?: string
  autoSignOn?: boolean
}

export const getSignOnText = (
  record: SignOnRecord,
  formatDateString: FormatDateStringType
) => {
  const {
    workerType,
    signOnType,
    createdAt,
    manualSignedOnAt,
    formName,
    autoSignOn,
  } = record

  let subtitle = workerType === WORKER_TYPES.VISITOR ? 'Visitor' : null
  let signedOnAt = createdAt

  if (signOnType === SIGN_ON_TYPES.GATE_ACCESS_CREATED_SIGN_ON) {
    subtitle = [subtitle, 'Gate access sign on'].filter(Boolean).join(' - ')
  }

  if (signOnType === SIGN_ON_TYPES.MANUALLY_CREATED_SIGN_ON) {
    subtitle = [subtitle, 'Manual sign on'].filter(Boolean).join(' - ')
    signedOnAt = manualSignedOnAt || signedOnAt
  } else if (!!manualSignedOnAt && manualSignedOnAt !== createdAt) {
    signedOnAt = manualSignedOnAt
    const date = formatDateString({
      date: createdAt,
      includeDate: false,
    })

    subtitle = [subtitle, `Overrides app sign on ${date}`]
      .filter(Boolean)
      .join(' - ')
  }

  if (signOnType === SIGN_ON_TYPES.FORM_CREATED_SIGN_ON) {
    subtitle = [subtitle, `Signed on via form: ${formName}`]
      .filter(Boolean)
      .join(' - ')
  }

  if (autoSignOn) {
    subtitle = [subtitle, 'Auto sign on'].filter(Boolean).join(' - ')
  }

  return {
    signedOnAt,
    subtitle,
  }
}

type SignOffRecord = {
  workerType: string
  signOnType: string
  signedOffAt: string
  manualSignedOffAt?: string
  autoSignOff?: boolean
}

export const getSignOffText = (
  record: SignOffRecord,
  formatDateString: FormatDateStringType
) => {
  const {
    workerType,
    signOnType,
    signedOffAt,
    manualSignedOffAt,
    autoSignOff,
  } = record
  record

  if (!signedOffAt && !manualSignedOffAt) {
    return { signedOffAt: '', subtitle: '' }
  }

  let subtitle = workerType === WORKER_TYPES.VISITOR ? 'Visitor' : null
  let finalSignedOffAt = signedOffAt

  if (signOnType === SIGN_ON_TYPES.MANUALLY_CREATED_SIGN_ON) {
    subtitle = [subtitle, 'Manual sign off'].filter(Boolean).join(' - ')
    finalSignedOffAt = manualSignedOffAt || finalSignedOffAt
  } else if (signOnType === SIGN_ON_TYPES.GATE_ACCESS_CREATED_SIGN_ON) {
    subtitle = [subtitle, 'Gate access sign off'].filter(Boolean).join(' - ')
    finalSignedOffAt = manualSignedOffAt || finalSignedOffAt
  } else if (!!manualSignedOffAt && !signedOffAt) {
    finalSignedOffAt = manualSignedOffAt
    subtitle = [subtitle, 'Manual sign off'].filter(Boolean).join(' - ')
  } else if (!!manualSignedOffAt && manualSignedOffAt !== signedOffAt) {
    finalSignedOffAt = manualSignedOffAt

    const date = formatDateString({
      date: finalSignedOffAt,
      includeDate: false,
    })

    subtitle = [subtitle, `Overrides app sign off ${date}`]
      .filter(Boolean)
      .join(' - ')
  }

  if (autoSignOff) {
    subtitle = [subtitle, 'Auto sign off'].filter(Boolean).join(' - ')
  }

  return {
    signedOffAt: finalSignedOffAt,
    subtitle,
  }
}
